import { useState, useEffect } from 'react'
import { Share2, Loader2, Bell, MessageSquare } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { UserSearchCombobox } from './UserSearchCombobox'
import { PeopleWithAccessList } from './PeopleWithAccessList'
import { templatePermissionService, externalInvitationService } from '@/lib/api/services'
import type {
  TemplatePermission,
  PermissionLevel,
} from '@/lib/api/types'

interface SelectedRecipient {
  id: string
  email: string
  fullName: string
  avatarUrl?: string
  isExternal: boolean
}

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  templateName: string
  currentUserId?: string
}

const PERMISSION_OPTIONS: { value: PermissionLevel; label: string; description: string }[] = [
  { value: 'View', label: 'Can view', description: 'Can view and use the template' },
  { value: 'Edit', label: 'Can edit', description: 'Can modify the template' },
  { value: 'Admin', label: 'Can manage', description: 'Full access including permissions' },
]

export function ShareDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  currentUserId,
}: ShareDialogProps) {
  const [selectedRecipients, setSelectedRecipients] = useState<SelectedRecipient[]>([])
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('View')
  const [notifyPeople, setNotifyPeople] = useState(true)
  const [personalMessage, setPersonalMessage] = useState('')
  const [showMessageInput, setShowMessageInput] = useState(false)
  const [permissions, setPermissions] = useState<TemplatePermission[]>([])
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (open) {
      loadPermissions()
    }
  }, [open, templateId])

  const loadPermissions = async () => {
    setLoading(true)
    try {
      const detail = await templatePermissionService.getPermissions(templateId)
      setPermissions(detail.permissions)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load permissions',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleShare = async () => {
    if (selectedRecipients.length === 0) return

    setSharing(true)
    const results: { success: boolean; email: string; error?: string }[] = []

    try {
      // Process internal users
      const internalRecipients = selectedRecipients.filter((r) => !r.isExternal)
      for (const recipient of internalRecipients) {
        try {
          await templatePermissionService.grantPermission(templateId, {
            scopeType: 'User',
            scopeId: recipient.id,
            permissionLevel,
            sendNotification: notifyPeople,
            personalMessage: showMessageInput ? personalMessage : undefined,
          })
          results.push({ success: true, email: recipient.email })
        } catch (error) {
          results.push({
            success: false,
            email: recipient.email,
            error: error instanceof Error ? error.message : 'Failed to share',
          })
        }
      }

      // Process external users
      const externalRecipients = selectedRecipients.filter((r) => r.isExternal)
      for (const recipient of externalRecipients) {
        try {
          await externalInvitationService.inviteExternalUser(templateId, {
            email: recipient.email,
            permissionLevel,
            personalMessage: showMessageInput ? personalMessage : undefined,
          })
          results.push({ success: true, email: recipient.email })
        } catch (error) {
          results.push({
            success: false,
            email: recipient.email,
            error: error instanceof Error ? error.message : 'Failed to send invitation',
          })
        }
      }

      // Show results
      const successCount = results.filter((r) => r.success).length
      const failCount = results.filter((r) => !r.success).length

      if (successCount > 0 && failCount === 0) {
        toast({
          title: 'Shared successfully',
          description: `Template shared with ${successCount} ${successCount === 1 ? 'person' : 'people'}`,
        })
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: 'Partially shared',
          description: `Shared with ${successCount} ${successCount === 1 ? 'person' : 'people'}, failed for ${failCount}`,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Failed to share',
          description: results[0]?.error || 'Unknown error',
          variant: 'destructive',
        })
      }

      // Clear selections and refresh permissions
      setSelectedRecipients([])
      setPersonalMessage('')
      setShowMessageInput(false)
      await loadPermissions()
    } finally {
      setSharing(false)
    }
  }

  const handleUpdatePermission = async (permissionId: string, level: PermissionLevel) => {
    try {
      await templatePermissionService.updatePermission(templateId, permissionId, {
        permissionLevel: level,
      })
      await loadPermissions()
      toast({
        title: 'Permission updated',
        description: 'The permission level has been updated',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update permission',
        variant: 'destructive',
      })
    }
  }

  const handleRevokePermission = async (permissionId: string) => {
    try {
      await templatePermissionService.revokePermission(templateId, permissionId)
      await loadPermissions()
      toast({
        title: 'Access removed',
        description: 'The user no longer has access to this template',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove access',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share "{templateName}"
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Search and add people */}
          <div className="space-y-3">
            <UserSearchCombobox
              selectedRecipients={selectedRecipients}
              onRecipientsChange={setSelectedRecipients}
              disabled={sharing}
            />

            {selectedRecipients.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="permission-level" className="text-sm text-muted-foreground whitespace-nowrap">
                    Permission:
                  </Label>
                  <Select
                    value={permissionLevel}
                    onValueChange={(value) => setPermissionLevel(value as PermissionLevel)}
                    disabled={sharing}
                  >
                    <SelectTrigger id="permission-level" className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERMISSION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="notify"
                    checked={notifyPeople}
                    onCheckedChange={(checked) => setNotifyPeople(checked as boolean)}
                    disabled={sharing}
                  />
                  <Label htmlFor="notify" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
                    <Bell className="h-3.5 w-3.5" />
                    Notify
                  </Label>
                </div>
              </div>
            )}

            {selectedRecipients.length > 0 && (
              <div className="space-y-2">
                {!showMessageInput ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => setShowMessageInput(true)}
                    disabled={sharing}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Add a message
                  </Button>
                ) : (
                  <Textarea
                    placeholder="Add a personal message (optional)"
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    rows={2}
                    disabled={sharing}
                  />
                )}
              </div>
            )}

            {selectedRecipients.length > 0 && (
              <div className="flex justify-end">
                <Button
                  onClick={handleShare}
                  disabled={sharing || selectedRecipients.length === 0}
                >
                  {sharing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sharing...
                    </>
                  ) : (
                    'Share'
                  )}
                </Button>
              </div>
            )}
          </div>

          <Separator />

          {/* People with access */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">People with access</Label>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <PeopleWithAccessList
                permissions={permissions}
                currentUserId={currentUserId}
                canManage={true}
                onUpdatePermission={handleUpdatePermission}
                onRevokePermission={handleRevokePermission}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
